import { useRef, useState } from "react";
import { ImagePlus, Link2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { COPPER } from "../VestaAuth";
import { B, EXTRA, TIPI, mediaSrc, uploadMedia } from "./common";
import { LinkCard } from "./Media";

const inp = "w-full rounded-xl border border-[#2A303C] bg-[#121418] px-3 py-2 text-sm text-white outline-none transition-[border-color] focus:border-[#C17F59]";

function MediaPicker({ media, setMedia }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState([]);
  const add = async (files) => {
    const list = [...files].slice(0, 6 - media.length - busy.length);
    if (files.length > list.length) toast.error("Massimo 6 file per post");
    for (const f of list) {
      const k = `${f.name}-${Math.random()}`;
      setBusy((b) => [...b, { k, name: f.name, p: 0 }]);
      try {
        const r = await uploadMedia(f, (p) => setBusy((b) => b.map((x) => (x.k === k ? { ...x, p } : x))));
        setMedia((m) => [...m, r]);
      } catch (e) { toast.error(e.response?.data?.detail || `Caricamento di ${f.name} non riuscito`); }
      setBusy((b) => b.filter((x) => x.k !== k));
    }
  };
  return (
    <>
      <input ref={ref} type="file" multiple accept="image/*,video/*" className="hidden" data-testid="bacheca-media-input" onChange={(e) => { add(e.target.files || []); e.target.value = ""; }} />
      {(media.length > 0 || busy.length > 0) && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="bacheca-media-preview">
          {media.map((m, i) => (
            <div key={m.url} className="relative aspect-square overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
              {m.kind === "video" ? <video src={mediaSrc(m.url)} className="h-full w-full object-cover" muted playsInline /> : <img src={mediaSrc(m.url)} alt="" className="h-full w-full object-cover" />}
              <button type="button" onClick={() => setMedia(media.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/70 p-1" data-testid={`bacheca-media-remove-${i}`}><X className="h-3 w-3" /></button>
            </div>
          ))}
          {busy.map((b) => (
            <div key={b.k} className="flex aspect-square flex-col items-center justify-center rounded-lg border border-neutral-800 text-xs text-neutral-400">
              <Loader2 className="mb-1 h-4 w-4 animate-spin" style={{ color: COPPER }} />{b.p}%
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={() => ref.current?.click()} disabled={media.length + busy.length >= 6} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-neutral-300 transition-colors hover:text-[#C17F59] disabled:opacity-40" data-testid="bacheca-add-media">
        <ImagePlus className="h-4 w-4" /> Foto / video
      </button>
    </>
  );
}

function LinkPicker({ links, setLinks }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!url.trim() || links.length >= 3) return;
    setBusy(true);
    try { const r = await api.post(`${B}/link-preview`, { url }); setLinks([...links, r.data]); setUrl(""); } catch { toast.error("Link non valido"); }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      {links.map((l, i) => <LinkCard key={i} l={l} onRemove={() => setLinks(links.filter((_, j) => j !== i))} />)}
      <div className="flex gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900 px-3">
          <Link2 className="h-4 w-4 text-neutral-500" />
          <input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Incolla un link (Instagram, YouTube, Facebook…)" className="h-9 w-full min-w-0 bg-transparent text-sm text-white outline-none" data-testid="bacheca-link-input" />
        </div>
        <button type="button" onClick={add} disabled={busy || !url.trim()} className="shrink-0 rounded-lg border border-neutral-800 px-3 text-xs font-bold text-neutral-300 hover:border-[#C17F59] disabled:opacity-40" data-testid="bacheca-link-add">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aggiungi"}</button>
      </div>
    </div>
  );
}

export default function ComposerForm({ me, onCreated, onCancel, initialTipo = "discussione" }) {
  const [tipo, setTipo] = useState(initialTipo);
  const [f, setF] = useState({ titolo: "", testo: "", extra: {} });
  const [media, setMedia] = useState([]);
  const [links, setLinks] = useState([]);
  const [saving, setSaving] = useState(false);
  const tipi = Object.entries(TIPI).filter(([k]) => k !== "comunicazione" || me?.mod);
  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.post(`${B}/posts`, { tipo, ...f, media, links: links.map((l) => l.url) });
      onCreated(r.data); toast.success("Pubblicato");
    } catch (e) { toast.error(e.response?.data?.detail || "Pubblicazione non riuscita"); }
    setSaving(false);
  };
  return (
    <div className="flex h-full flex-col overflow-x-hidden" data-testid="bacheca-composer">
      <div className="flex items-center justify-between border-b border-[#2A303C] px-4 py-3">
        <button type="button" onClick={onCancel} className="text-sm font-semibold text-[#9CA3AF] hover:text-white" data-testid="bacheca-composer-cancel">Annulla</button>
        <span className="font-heading text-base font-bold">Nuovo post</span>
        <button type="button" onClick={submit} disabled={saving} data-testid="bacheca-composer-submit"
          className="rounded-full px-5 py-1.5 text-sm font-bold text-white transition-[opacity,transform] hover:-translate-y-0.5 disabled:opacity-50" style={{ background: COPPER }}>
          {saving ? "Pubblico…" : "Pubblica"}
        </button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none]">
          {tipi.map(([k, t]) => {
            const I = t.icon;
            return (
              <button key={k} type="button" onClick={() => setTipo(k)} data-testid={`bacheca-composer-tipo-${k}`}
                className="flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors"
                style={tipo === k ? { borderColor: COPPER, color: "#fff", background: COPPER } : { borderColor: "#2A303C", color: "#d4d4d4", background: "#16191E" }}>
                <I className="h-3.5 w-3.5" />{t.label}
              </button>
            );
          })}
        </div>
        <input className={inp} placeholder={tipo === "mercatino" ? "Cosa vendi?" : "Titolo (facoltativo)"} value={f.titolo} onChange={(e) => setF({ ...f, titolo: e.target.value })} data-testid="bacheca-composer-titolo" />
        <textarea rows={5} className={`${inp} text-[15px]`} placeholder="A cosa stai lavorando?" value={f.testo} onChange={(e) => setF({ ...f, testo: e.target.value })} data-testid="bacheca-composer-testo" />
        {EXTRA[tipo] && (
          <div className="grid gap-2 sm:grid-cols-2">
            {EXTRA[tipo].map(([k, l]) => <input key={k} className={inp} placeholder={l} value={f.extra[k] || ""} onChange={(e) => setF({ ...f, extra: { ...f.extra, [k]: e.target.value } })} data-testid={`bacheca-composer-extra-${k}`} />)}
          </div>
        )}
        <MediaPicker media={media} setMedia={setMedia} />
        <LinkPicker links={links} setLinks={setLinks} />
        {tipo === "impianto" && <p className="text-[11px] text-[#6B7280]">Evita foto con indirizzi, targhe o volti dei clienti finali.</p>}
      </div>
    </div>
  );
}
